import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

import { Card, CardContent } from '@nucleus-commerce/ui/components/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@nucleus-commerce/ui/components/collapsible';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export interface SummaryCardProps {
  title: ReactNode;
  /** Beside the title: a Shared or This store chip. */
  badge?: ReactNode;
  /** The far end of the title row: Edit, Edit on master. */
  action?: ReactNode;
  /** One line under the title. */
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** `data-tour` value when a guided tour points at this card. */
  tour?: string;
  id?: string;
  /** Text at the far end of a collapsible card's title row: "3 of 4 live". */
  meta?: ReactNode;
  /** The title row toggles the body. Closed by default. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CARD = 'gap-2.5 py-4';
const BODY = 'flex flex-col gap-2 px-4 text-sm';

/**
 * A compact card of the product pages: a title row, then a short summary of the section or the
 * fields themselves. The edit itself usually opens in an `EditModal` from `action`.
 */
export function SummaryCard({
  title,
  badge,
  action,
  description,
  children,
  className,
  tour,
  id,
  meta,
  collapsible = false,
  defaultOpen,
  open,
  onOpenChange,
}: SummaryCardProps) {
  const heading = <span className="text-[15px] leading-tight font-semibold">{title}</span>;
  if (collapsible) {
    return (
      <Collapsible asChild open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
        <Card id={id} data-tour={tour} className={cn(CARD, className)}>
          <CollapsibleTrigger className="group/summary flex w-full items-center gap-2 rounded-md px-4 text-left">
            {heading}
            {badge}
            <span className="flex-1" />
            {meta ? <span className="text-muted-foreground text-[13px]">{meta}</span> : null}
            <ChevronRight
              aria-hidden
              className="text-muted-foreground size-4 shrink-0 transition-transform group-data-[state=open]/summary:rotate-90"
            />
          </CollapsibleTrigger>
          <CollapsibleContent asChild>
            <CardContent className={BODY}>{children}</CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    );
  }
  return (
    <Card id={id} data-tour={tour} className={cn(CARD, className)}>
      <div className="flex flex-wrap items-center gap-2 px-4">
        {heading}
        {badge}
        <span className="flex-1" />
        {action}
      </div>
      {description ? (
        <p className="text-muted-foreground -mt-1 px-4 text-[13px]">{description}</p>
      ) : null}
      {children ? <CardContent className={BODY}>{children}</CardContent> : null}
    </Card>
  );
}
